import cn from 'classnames';
import { FC, useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import {
  type ComparePane,
  type ComparePaneState,
  MedicalImageRender,
  ViewMIR,
} from '@/shared/graphics/medicalImageRender/MedicalImageRender';
import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Button, Modal, Spinner } from '@/shared/ui';

import { ModalID, modalModel, type StudyCompareImage } from '@/entities/modal';
import {
  type StudyAccordionOption,
  StudyAccordionSelect,
} from '@/entities/study';

import {
  useComparePaneInput,
  useCompareView,
  useReportPanoramas,
} from '../../hooks';

import styles from './StudyCompareModal.module.scss';

const toOption = (image: StudyCompareImage): StudyAccordionOption => ({
  id: image.reportID,
  previewSrc: image.previewSrc,
  contain: true,
  triggerLabel: `${image.title} · ${image.date}`,
  title: image.title,
  description: image.date,
});

const PANES: ComparePane[] = ['left', 'right'];

// "error" means the pane will stay blank, so it is no longer worth waiting for
const isPaneStatePending = (state: ComparePaneState) =>
  state !== 'ready' && state !== 'error';

export const StudyCompareModal: FC = () => {
  const { formatMessage } = useIntl();
  const dispatch = useAppDispatch();
  const {
    visible,
    data: { images, mainID: defaultMainID, secondaryID: defaultSecondaryID },
  } = useAppSelector(modalModel.selectors.selectStudyCompareModal);

  const [mainID, setMainID] = useState(defaultMainID);
  const [secondaryID, setSecondaryID] = useState(defaultSecondaryID);
  const [showMasks, setShowMasks] = useState(true);

  const isPanoramasPending = useReportPanoramas([mainID, secondaryID], visible);
  const { isViewReady, transform, paneStates } = useCompareView(visible);
  const mainInput = useComparePaneInput(mainID);
  const secondaryInput = useComparePaneInput(secondaryID);

  // The pane stays blank until its panorama is fetched, decoded and drawn, so
  // the indicator has to live in the pane. A failed load, or a report that has
  // no panorama at all, must not spin forever.
  const paneLoading: Record<ComparePane, boolean> = {
    left:
      isPanoramasPending ||
      (Boolean(mainInput) && isPaneStatePending(paneStates.left)),
    right:
      isPanoramasPending ||
      (Boolean(secondaryInput) && isPaneStatePending(paneStates.right)),
  };

  // Each opening starts from the pair the page proposed.
  useEffect(() => {
    if (visible) {
      setMainID(defaultMainID);
      setSecondaryID(defaultSecondaryID);
      setShowMasks(true);
    }
  }, [visible, defaultMainID, defaultSecondaryID]);

  useEffect(() => {
    if (isViewReady && mainInput) {
      MedicalImageRender.CompareAPI.setComparePane('left', mainInput);
    }
  }, [isViewReady, mainInput]);

  useEffect(() => {
    if (isViewReady && secondaryInput) {
      MedicalImageRender.CompareAPI.setComparePane('right', secondaryInput);
    }
  }, [isViewReady, secondaryInput]);

  useEffect(() => {
    if (isViewReady) {
      MedicalImageRender.CompareAPI.setCompareMasksVisible(showMasks);
    }
  }, [isViewReady, showMasks]);

  const handleClose = () => {
    dispatch(modalModel.actions.closeModal(ModalID.StudyCompare));
  };

  if (!images.some((image) => image.reportID === mainID)) {
    return null;
  }

  const mainOptions = images
    .filter((image) => image.reportID !== secondaryID)
    .map(toOption);
  const secondaryOptions = images
    .filter((image) => image.reportID !== mainID)
    .map(toOption);

  return (
    <Modal
      isOpen={visible}
      title={formatMessage({
        id: 'patientProfile.compare.title',
        defaultMessage: 'Compare studies',
      })}
      className={styles.root}
      containerClassName={styles.container}
      bodyClassName={styles.body}
      dataComponent="study-compare-modal"
      hideFooter
      onCancel={handleClose}
    >
      <div className={styles.headers}>
        <div className={styles.header} data-component="study-compare-main">
          <StudyAccordionSelect
            className={styles.selector}
            options={mainOptions}
            value={mainID}
            onChange={setMainID}
          />
        </div>

        <div className={styles.header} data-component="study-compare-secondary">
          <StudyAccordionSelect
            className={styles.selector}
            options={secondaryOptions}
            value={secondaryID}
            onChange={setSecondaryID}
          />
          {transform.hasAdjustment && (
            <Button
              variant="tertiary"
              size="small"
              className={styles.headerAction}
              onClick={() =>
                MedicalImageRender.CompareAPI.resetCompareAdjustment()
              }
            >
              <FormattedMessage
                id="patientProfile.compare.resetAdjustment"
                defaultMessage="Reset adjustment"
              />
            </Button>
          )}
        </div>
      </div>

      {isViewReady && (
        <div data-component="study-compare-view" className={styles.viewHost}>
          <div className={styles.viewFrame}>
            <ViewMIR viewName="compare" className={styles.view} />

            <div className={styles.paneLoaders} aria-hidden="true">
              {PANES.map((pane) => (
                <div
                  key={pane}
                  className={styles.paneLoader}
                  data-component={`study-compare-loader-${pane}`}
                >
                  {paneLoading[pane] && <Spinner size={44} primary />}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className={styles.toolbar}>
        <span className={cn('p3', styles.hint)}>
          {isPanoramasPending && <Spinner size={20} />}
          {isPanoramasPending ? (
            <FormattedMessage
              id="patientProfile.compare.loading"
              defaultMessage="Loading full-size images and masks…"
            />
          ) : (
            <FormattedMessage
              id="patientProfile.compare.hint"
              defaultMessage="Scroll to zoom, drag to pan. The left image leads, the right one can be adjusted on its own."
            />
          )}
        </span>

        <div className={styles.toolbarActions}>
          <Button
            variant={showMasks ? 'primary' : 'secondary'}
            size="small"
            onClick={() => setShowMasks((current) => !current)}
          >
            <FormattedMessage
              id="global.showMasks"
              defaultMessage="Show Masks"
            />
          </Button>

          <Button
            variant="secondary"
            size="small"
            disabled={transform.isPristine}
            onClick={() => MedicalImageRender.CompareAPI.resetCompare()}
          >
            <FormattedMessage id="global.reset" defaultMessage="Reset" />
          </Button>
        </div>
      </div>
    </Modal>
  );
};
